import React from 'react';
import { Box, Drawer, Popover, Radio, RadioGroup, FormControlLabel, Divider } from '@mui/material';
import { colors, spacing, radius, FontWeight } from 'common/theme';
import { TextHot } from 'components/common/TextHot';
import { ButtonHot, BUTTON_MODE } from 'components/common/ButtonHot';
import TuneIcon from '@mui/icons-material/Tune';
import { generateTestId } from 'app/functions';
import { useTranslations } from 'common/hooks/useTranslations';
import { FILTER_BENEFITS_TYPES } from 'pages/personal-zone/utils/enums';
const FilterContent = ({
  testID,
  filters,
  onFilterChange,
  onApply,
  onReset,
  onClose,
  isMobile,
  filterMode = 'paid',
  filterCounts = {},
}) => {
  const showPaidFilters = filterMode === 'paid';
  const { translate } = useTranslations();
  const selectedValue = filters?.selectedFilter || FILTER_BENEFITS_TYPES.ACTIVE;
  
  const {
    totalActiveBenefitsCount = 0,
    totalCancelledBenefitsCount = 0,
    totalArchivedBenefitsCount = 0,
  } = filterCounts;
  
  const showActiveFilter = totalActiveBenefitsCount > 0;
  const showCancelledFilter = showPaidFilters && totalCancelledBenefitsCount > 0;
  const showArchivedFilter = showPaidFilters && totalArchivedBenefitsCount > 0;

  const handleChange = (event) => {
    const value = event.target.value;
    onFilterChange(value);
  };

  const handleApply = () => {
    onApply?.();
  };

  return (
    <Box dir="rtl" testId={generateTestId(testID, 'content')} sx={{ display: 'flex', flexDirection: 'column', padding: `${spacing.lg}px`, backgroundColor: colors.palette.white, minWidth: isMobile ? 'auto' : 300 }}>
      <Box testId={generateTestId(testID, 'header')} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: `${spacing.sm}px` }}>
        <TuneIcon sx={{ color: colors.system.text.primary, fontSize: spacing.xxl }} />
        <TextHot testId={generateTestId(testID, 'title')} size="l" bold="bold" color="primary">
          {translate('filter_sheet_title')}
        </TextHot>
      </Box>

      <Box testId={generateTestId(testID, 'radio-group')} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingY: `${spacing.md}px` }}>
        <RadioGroup
          value={selectedValue}
          onChange={handleChange}
          sx={{ width: '100%' }}
        >
          {showActiveFilter && (
            <FormControlLabel
              value={FILTER_BENEFITS_TYPES.ACTIVE}
              control={
                <Radio
                  sx={{ color: colors.system.border.default, '&.Mui-checked': { color: colors.system.brand.primary } }}
                />
              }
              label={<TextHot testId={generateTestId(testID, 'active-label')} size="l" color="primary">{translate('my_coupons_available')}</TextHot>}
              sx={{ width: '100%', justifyContent: 'flex-start', margin: 0 }}
            />
          )}

          {showCancelledFilter && (
            <FormControlLabel
              value={FILTER_BENEFITS_TYPES.CANCELLED}
              control={
                <Radio
                  sx={{ color: colors.system.border.default, '&.Mui-checked': { color: colors.system.brand.primary } }}
                />
              }
              label={<TextHot testId={generateTestId(testID, 'cancelled-label')} size="l" color="primary">{translate('my_coupons_cancelled_filter_label')}</TextHot>}
              sx={{ width: '100%', justifyContent: 'flex-start', margin: 0 }}
            />
          )}

          {showArchivedFilter && (
            <FormControlLabel
              value={FILTER_BENEFITS_TYPES.ARCHIVED}
              control={
                <Radio
                  sx={{ color: colors.system.border.default, '&.Mui-checked': { color: colors.system.brand.primary } }}
                />
              }
              label={<TextHot testId={generateTestId(testID, 'archive-label')} size="l" color="primary">{translate('my_coupons_archived_filter_label')}</TextHot>}
              sx={{ width: '100%', justifyContent: 'flex-start', margin: 0 }}
            />
          )}
        </RadioGroup>
      </Box>

      <Divider testId={generateTestId(testID, 'divider')} sx={{ borderColor: colors.system.border.default }} />

      <Box testId={generateTestId(testID, 'button-group')} sx={{ display: 'flex', flexDirection: 'row-reverse', gap: `${spacing.md}px`, paddingTop: `${spacing.lg}px` }}>
        <ButtonHot
          testId={generateTestId(testID, 'apply-btn')}
          mode={BUTTON_MODE.PRIMARY}
          onClick={handleApply}
          sx={{ 
            flex: 1, 
            borderRadius: `${radius.xl}px`
          }}
        >
          {translate('filter_sheet_apply_button')}
        </ButtonHot>
        <ButtonHot
          testId={generateTestId(testID, 'reset-btn')}
          mode={BUTTON_MODE.TEXT}
          onClick={onReset}
          sx={{ flex: isMobile ? 1 : 'none', minWidth: 80, color: colors.system.text.primary, fontWeight: FontWeight.bold }}
        >
          {translate('category_reset_button')}
        </ButtonHot>
      </Box>
    </Box>
  );
};

export const FilterBottomSheet = (props) => {
  const { open, onClose, isMobile, anchorEl, testID, ...rest } = props;
  
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom" 
        open={open} 
        onClose={onClose}
        testId={generateTestId(testID, 'drawer')}
        PaperProps={{ sx: { borderRadius: `${radius.xl}px ${radius.xl}px 0 0`, maxHeight: '80vh' } }}
      >
        <FilterContent testID={testID} {...rest} onClose={onClose} isMobile={true} />
      </Drawer>
    );
  }

  return (
    <Popover
      open={open} 
      anchorEl={anchorEl} 
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ 
        sx: { 
          borderRadius: `${radius.lg}px`, 
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)', 
          marginTop: `${spacing.sm}px` 
        } 
      }}
    >
      <FilterContent testID={testID} {...rest} onClose={onClose} isMobile={false} />
    </Popover>
  );
};

export default FilterBottomSheet;